import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Settings as SettingsIcon,
    ShieldCheck,
    ShieldAlert,
    Radio,
    CircleOff,
    KeyRound,
    Mail,
    Inbox,
    Pencil,
    X as Xicon,
    Save,
    Check,
    AlertTriangle,
    Loader2,
    Eye,
    EyeOff,
    Link as LinkIcon,
    ChevronDown,
    ChevronRight,
    RefreshCw,
    ScrollText,
    FileText,
    Copy,
    RotateCcw,
    Play,
} from "lucide-react";
import { toast } from "sonner";

import api from "@/api";

// Relative time formatter (small, no external dep needed).
function relTime(iso) {
    if (!iso) return "never";
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) return iso;
    const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
}

function truncate(s, n) {
    if (!s) return "";
    return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

// ---------------------------------------------------------------------------
// Field catalog — display labels + placeholders for each editable config key.
// Grouped by category; rendered in the same section order as the backend.
// ---------------------------------------------------------------------------
const GROUPS = [
    {
        key: "core",
        title: "core",
        blurb: "Ingress secret, Glip fallback URL, and public base URL.",
        keys: [
            {
                key: "SAMSARA_WEBHOOK_SECRET",
                label: "Samsara webhook secret",
                placeholder: "shared HMAC secret from Samsara",
                icon: ShieldCheck,
                iconOff: ShieldAlert,
                hint: "When set, /webhook/samsara enforces HMAC-SHA256 over the raw body.",
            },
            {
                key: "RINGCENTRAL_WEBHOOK_URL",
                label: "RingCentral fallback URL",
                placeholder: "https://hooks.glip.com/webhook/...",
                icon: Radio,
                iconOff: CircleOff,
                hint: "Used only when no rule matches (env-default channel).",
            },
            {
                key: "PUBLIC_BASE_URL",
                label: "Public base URL",
                placeholder: "https://your-host.example.com",
                icon: LinkIcon,
                iconOff: LinkIcon,
                hint: "Used to build shortened \"See Details\" URLs in emails.",
            },
        ],
    },
    {
        key: "imap",
        title: "email ingress (IMAP)",
        blurb: "Mailbox the poller watches for Samsara alert emails.",
        keys: [
            { key: "IMAP_HOST", label: "Host", placeholder: "imap.gmail.com" },
            { key: "IMAP_PORT", label: "Port", placeholder: "993" },
            { key: "IMAP_USERNAME", label: "Username", placeholder: "inbox@yourdomain.com" },
            { key: "IMAP_PASSWORD", label: "Password / app password", placeholder: "•••• •••• •••• ••••" },
            { key: "IMAP_MAILBOX", label: "Mailbox", placeholder: "INBOX" },
            {
                key: "IMAP_SENDER_FILTER",
                label: "Sender filter",
                placeholder: "noreply@samsara.com",
            },
            {
                key: "IMAP_POLL_INTERVAL_SECONDS",
                label: "Poll interval (s)",
                placeholder: "60",
            },
        ],
    },
    {
        key: "smtp",
        title: "email egress (SMTP)",
        blurb: "Outgoing SMTP relay used for email-type channels.",
        keys: [
            { key: "SMTP_HOST", label: "Host", placeholder: "smtp.gmail.com" },
            { key: "SMTP_PORT", label: "Port", placeholder: "587" },
            { key: "SMTP_USERNAME", label: "Username", placeholder: "relay@yourdomain.com" },
            { key: "SMTP_PASSWORD", label: "Password / app password", placeholder: "•••• •••• •••• ••••" },
            { key: "SMTP_FROM", label: "From address", placeholder: "relay@yourdomain.com" },
            { key: "SMTP_FROM_NAME", label: "From name", placeholder: "Samsara Hub" },
        ],
    },
];

// ---------------------------------------------------------------------------
// Presentational building blocks
// ---------------------------------------------------------------------------
function SectionHeader({ title, blurb }) {
    return (
        <div className="mt-8 mb-3">
            <div
                className="mono text-[10px] uppercase tracking-[0.2em]"
                style={{ color: "var(--text-faint)" }}
            >
                {title}
            </div>
            {blurb ? (
                <div
                    className="mono text-[11px] mt-1"
                    style={{ color: "var(--text-dim)" }}
                >
                    {blurb}
                </div>
            ) : null}
        </div>
    );
}

function SourceBadge({ source, isSet }) {
    if (!isSet) {
        return (
            <span
                className="mono text-[10px] uppercase tracking-[0.15em] px-2 py-[2px] rounded-full"
                style={{
                    color: "var(--text-faint)",
                    border: "1px solid var(--border)",
                }}
                data-testid="source-badge-unset"
            >
                unset
            </span>
        );
    }
    const color = source === "db" ? "var(--accent)" : "var(--info)";
    return (
        <span
            className="mono text-[10px] uppercase tracking-[0.15em] px-2 py-[2px] rounded-full"
            style={{
                color,
                border: `1px solid ${color}`,
                background: "transparent",
            }}
            data-testid={`source-badge-${source}`}
        >
            {source}
        </span>
    );
}

function ConfigRow({ field, descriptor, onSaved }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState("");
    const [reveal, setReveal] = useState(false);
    const [saving, setSaving] = useState(false);

    const isSecret = descriptor.is_secret;
    const isSet = descriptor.is_set;

    const displayValue = useMemo(() => {
        if (!isSet) return "— unset —";
        if (isSecret) return descriptor.masked || "••••";
        return descriptor.value || "";
    }, [descriptor, isSet, isSecret]);

    const beginEdit = () => {
        setVal("");
        setEditing(true);
    };
    const cancel = () => {
        setEditing(false);
        setVal("");
    };

    const save = async (clear = false) => {
        setSaving(true);
        try {
            const body = { [field.key]: clear ? "" : val };
            const resp = await api.patch("/admin/config", body);
            toast.success(
                `${field.label} ${clear ? "cleared" : "saved"}${
                    resp?.data?.imap_restarted ? " · IMAP restarted" : ""
                }`,
                { duration: 2500 },
            );
            setEditing(false);
            setVal("");
            await onSaved();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "save failed";
            toast.error(`${field.label}: ${msg}`);
        } finally {
            setSaving(false);
        }
    };

    const Icon = field.icon || (isSet ? Check : CircleOff);

    return (
        <div
            className="panel-inset p-4"
            data-testid={`config-row-${field.key}`}
        >
            <div className="flex items-center gap-3 flex-wrap">
                <Icon
                    size={14}
                    style={{ color: isSet ? "var(--accent)" : "var(--text-faint)" }}
                />
                <div
                    className="mono text-[11px] uppercase tracking-[0.18em]"
                    style={{ color: "var(--text)", minWidth: 220 }}
                >
                    {field.label}
                </div>
                <SourceBadge source={descriptor.source} isSet={isSet} />
                <div className="flex-1" />
                {!editing && (
                    <>
                        <button
                            type="button"
                            onClick={beginEdit}
                            className="ghost-btn"
                            data-testid={`edit-btn-${field.key}`}
                        >
                            <Pencil size={11} /> edit
                        </button>
                        {isSet && descriptor.source === "db" && (
                            <button
                                type="button"
                                onClick={() => save(true)}
                                disabled={saving}
                                className="ghost-btn"
                                data-testid={`clear-btn-${field.key}`}
                                title="Clear DB override, revert to .env"
                            >
                                <Xicon size={11} /> revert
                            </button>
                        )}
                    </>
                )}
            </div>

            <div
                className="mono text-xs mt-2 break-all"
                style={{ color: isSet ? "var(--text-dim)" : "var(--text-faint)" }}
            >
                {editing ? null : displayValue}
            </div>

            {field.hint && !editing && (
                <div
                    className="mono text-[11px] mt-1"
                    style={{ color: "var(--text-faint)" }}
                >
                    {field.hint}
                </div>
            )}

            {editing && (
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-1 min-w-[260px]">
                        <input
                            className="console-input"
                            type={isSecret && !reveal ? "password" : "text"}
                            value={val}
                            placeholder={field.placeholder || ""}
                            onChange={(e) => setVal(e.target.value)}
                            data-testid={`edit-input-${field.key}`}
                            autoFocus
                        />
                        {isSecret && (
                            <button
                                type="button"
                                onClick={() => setReveal((r) => !r)}
                                className="ghost-btn"
                                title={reveal ? "Hide" : "Reveal"}
                            >
                                {reveal ? <EyeOff size={11} /> : <Eye size={11} />}
                            </button>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => save(false)}
                        disabled={saving || val.length === 0}
                        className="pill-btn"
                        data-testid={`save-btn-${field.key}`}
                    >
                        {saving ? (
                            <Loader2 size={11} className="animate-spin" />
                        ) : (
                            <Save size={11} />
                        )}{" "}
                        save
                    </button>
                    <button
                        type="button"
                        onClick={cancel}
                        disabled={saving}
                        className="ghost-btn"
                        data-testid={`cancel-btn-${field.key}`}
                    >
                        <Xicon size={11} /> cancel
                    </button>
                </div>
            )}
        </div>
    );
}

function TestButton({ label, endpoint, testid }) {
    const [result, setResult] = useState(null);
    const [running, setRunning] = useState(false);

    const run = async () => {
        setRunning(true);
        setResult(null);
        try {
            const resp = await api.post(endpoint, {});
            setResult(resp.data);
            if (resp.data?.ok) {
                toast.success(resp.data.message || `${label} ok`);
            } else {
                toast.error(`${label}: ${resp.data?.error || "failed"}`);
            }
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "failed";
            setResult({ ok: false, error: msg });
            toast.error(`${label}: ${msg}`);
        } finally {
            setRunning(false);
        }
    };

    const Icon = result?.ok ? Check : AlertTriangle;

    return (
        <div className="flex items-center gap-3 flex-wrap">
            <button
                type="button"
                onClick={run}
                disabled={running}
                className="pill-btn"
                data-testid={testid}
            >
                {running ? (
                    <Loader2 size={11} className="animate-spin" />
                ) : (
                    <Check size={11} />
                )}{" "}
                {running ? "testing…" : `test ${label}`}
            </button>
            {result && (
                <div
                    className="mono text-[11px] flex items-center gap-2"
                    style={{
                        color: result.ok ? "var(--accent)" : "var(--danger)",
                    }}
                    data-testid={`${testid}-result`}
                >
                    <Icon size={12} />
                    <span className="break-all">
                        {result.ok
                            ? result.message || "ok"
                            : result.error || "failed"}
                    </span>
                </div>
            )}
        </div>
    );
}

function ImapStatusBadge({ cfg }) {
    // 4 states: DISABLED · ERROR · POLLING · STARTING
    if (!cfg.imap_configured) {
        return (
            <span
                className="badge"
                data-testid="status-imap"
                style={{
                    color: "var(--text-faint)",
                    borderColor: "var(--border-strong)",
                }}
            >
                <span className="dot" />
                IMAP disabled
            </span>
        );
    }
    if (cfg.imap_last_error) {
        return (
            <span
                className="badge danger"
                data-testid="status-imap"
                title={`${cfg.imap_last_error}\nlast poll: ${cfg.imap_last_poll_at || "never"}`}
            >
                <span className="dot" />
                IMAP error: {truncate(cfg.imap_last_error, 80)}
            </span>
        );
    }
    if (cfg.imap_running && cfg.imap_last_poll_at) {
        return (
            <span
                className="badge ok"
                data-testid="status-imap"
                title={`last poll: ${cfg.imap_last_poll_at}`}
            >
                <span className="dot" />
                IMAP polling · last ok: {relTime(cfg.imap_last_poll_at)}
            </span>
        );
    }
    return (
        <span
            className="badge warn"
            data-testid="status-imap"
        >
            <span className="dot" />
            IMAP starting
        </span>
    );
}

function ImapHealthLine({ cfg }) {
    if (!cfg.imap_configured) return null;
    const r = cfg.imap_last_poll_result;
    if (!r) return null;
    return (
        <div
            className="mono text-[11px] mb-4"
            style={{ color: "var(--text-faint)" }}
            data-testid="imap-health-line"
        >
            last cycle: found={r.found ?? 0}, processed={r.processed ?? 0}
        </div>
    );
}

function AuditLog() {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [limit, setLimit] = useState(50);

    const load = useCallback(
        async (l = limit) => {
            setLoading(true);
            try {
                const resp = await api.get(`/admin/config/audit?limit=${l}`);
                setRows(resp.data?.audit || []);
            } catch (e) {
                toast.error(
                    `audit log: ${e?.response?.data?.detail || e?.message || "failed"}`,
                );
            } finally {
                setLoading(false);
            }
        },
        [limit],
    );

    useEffect(() => {
        if (open) load(limit);
    }, [open, limit, load]);

    return (
        <div className="mt-8" data-testid="audit-log-section">
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="ghost-btn"
                data-testid="audit-log-toggle"
            >
                {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                <ScrollText size={11} />
                {open ? "hide audit log" : "show audit log"}
            </button>

            {open && (
                <div
                    className="panel-inset p-4 mt-3"
                    data-testid="audit-log-panel"
                >
                    <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <span
                            className="mono text-[11px] uppercase tracking-[0.18em]"
                            style={{ color: "var(--text)" }}
                        >
                            config audit · newest first
                        </span>
                        <button
                            type="button"
                            onClick={() => load(limit)}
                            className="ghost-btn"
                            disabled={loading}
                            data-testid="audit-refresh-btn"
                        >
                            {loading ? (
                                <Loader2 size={11} className="animate-spin" />
                            ) : (
                                <RefreshCw size={11} />
                            )}
                            refresh
                        </button>
                        <span
                            className="mono text-[11px]"
                            style={{ color: "var(--text-faint)" }}
                        >
                            {rows.length} row{rows.length === 1 ? "" : "s"}
                        </span>
                    </div>

                    {rows.length === 0 && !loading ? (
                        <div
                            className="mono text-xs"
                            style={{ color: "var(--text-faint)" }}
                            data-testid="audit-empty"
                        >
                            — no config changes recorded yet —
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table
                                className="mono text-[11px] w-full"
                                data-testid="audit-table"
                            >
                                <thead
                                    style={{
                                        color: "var(--text-faint)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.15em",
                                    }}
                                >
                                    <tr>
                                        <th className="text-left py-1 pr-4">
                                            at
                                        </th>
                                        <th className="text-left py-1 pr-4">
                                            admin
                                        </th>
                                        <th className="text-left py-1 pr-4">
                                            key
                                        </th>
                                        <th className="text-left py-1 pr-4">
                                            action
                                        </th>
                                        <th className="text-left py-1 pr-4">
                                            from → to
                                        </th>
                                    </tr>
                                </thead>
                                <tbody style={{ color: "var(--text-dim)" }}>
                                    {rows.map((r) => (
                                        <tr
                                            key={r.id}
                                            style={{
                                                borderTop:
                                                    "1px solid var(--border)",
                                            }}
                                            data-testid={`audit-row-${r.id}`}
                                        >
                                            <td className="py-2 pr-4 align-top whitespace-nowrap">
                                                {r.at}
                                            </td>
                                            <td className="py-2 pr-4 align-top">
                                                {r.admin}
                                            </td>
                                            <td
                                                className="py-2 pr-4 align-top"
                                                style={{
                                                    color: "var(--text)",
                                                }}
                                            >
                                                {r.key}
                                            </td>
                                            <td
                                                className="py-2 pr-4 align-top"
                                                style={{
                                                    color:
                                                        r.action === "cleared"
                                                            ? "var(--warn)"
                                                            : r.action ===
                                                              "rotated"
                                                            ? "var(--info)"
                                                            : "var(--accent)",
                                                }}
                                            >
                                                {r.action}
                                            </td>
                                            <td className="py-2 pr-4 align-top break-all">
                                                {r.action === "rotated" ? (
                                                    <span
                                                        style={{
                                                            color: "var(--text-faint)",
                                                        }}
                                                    >
                                                        rotated
                                                    </span>
                                                ) : (
                                                    <>
                                                        <span
                                                            style={{
                                                                color: "var(--text-faint)",
                                                            }}
                                                        >
                                                            {r.old_value_masked ??
                                                                `— ${r.old_source} —`}
                                                        </span>
                                                        {" → "}
                                                        <span
                                                            style={{
                                                                color: "var(--text)",
                                                            }}
                                                        >
                                                            {r.new_value_masked ??
                                                                `— ${r.new_source} —`}
                                                        </span>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="mt-3">
                                <button
                                    type="button"
                                    className="ghost-btn"
                                    onClick={() => setLimit((l) => l + 50)}
                                    disabled={loading || rows.length < limit}
                                    data-testid="audit-load-more-btn"
                                >
                                    load 50 more
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function PasswordChange() {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (next.length < 4) {
            toast.error("new password must be at least 4 characters");
            return;
        }
        setSaving(true);
        try {
            await api.post("/admin/password", { current, new: next });
            toast.success("admin password updated — existing sessions remain valid");
            setCurrent("");
            setNext("");
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "failed";
            toast.error(`password: ${msg}`);
        } finally {
            setSaving(false);
        }
    };

    return (
        <form
            className="panel-inset p-4"
            onSubmit={submit}
            data-testid="password-change-form"
        >
            <div className="flex items-center gap-3 mb-3">
                <KeyRound size={14} style={{ color: "var(--accent)" }} />
                <span
                    className="mono text-[11px] uppercase tracking-[0.18em]"
                    style={{ color: "var(--text)" }}
                >
                    change admin password
                </span>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
                <input
                    className="console-input"
                    type="password"
                    placeholder="current password"
                    value={current}
                    onChange={(e) => setCurrent(e.target.value)}
                    data-testid="password-current-input"
                    required
                />
                <input
                    className="console-input"
                    type="password"
                    placeholder="new password (min 4 chars)"
                    value={next}
                    onChange={(e) => setNext(e.target.value)}
                    data-testid="password-new-input"
                    required
                />
            </div>
            <div className="mt-3 flex items-center gap-3 flex-wrap">
                <button
                    type="submit"
                    className="pill-btn"
                    disabled={saving || !current || !next}
                    data-testid="password-submit-btn"
                >
                    {saving ? (
                        <Loader2 size={11} className="animate-spin" />
                    ) : (
                        <Save size={11} />
                    )}{" "}
                    update password
                </button>
                <span
                    className="mono text-[11px]"
                    style={{ color: "var(--text-faint)" }}
                >
                    existing JWT sessions are NOT invalidated.
                </span>
            </div>
        </form>
    );
}

// ---------------------------------------------------------------------------
// Collapsible section wrapper (Patch J)
// ---------------------------------------------------------------------------
function CollapsibleSection({ slug, title, blurb, defaultOpen = false, children }) {
    const storageKey = `settings.section.${slug}`;
    const [open, setOpen] = useState(() => {
        try {
            const v = localStorage.getItem(storageKey);
            if (v === null) return defaultOpen;
            return v === "1";
        } catch {
            return defaultOpen;
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(storageKey, open ? "1" : "0");
        } catch {
            /* storage unavailable — still works in-memory for this session */
        }
    }, [open, storageKey]);

    return (
        <div className="mt-8" data-testid={`section-${slug}`}>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="w-full flex items-start gap-2 py-1 text-left transition-opacity hover:opacity-70"
                style={{ background: "transparent", border: "none" }}
                data-testid={`section-toggle-${slug}`}
            >
                <span className="pt-[2px]" style={{ color: "var(--text-faint)" }}>
                    {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </span>
                <span className="flex-1 min-w-0">
                    <span
                        className="mono text-[10px] uppercase tracking-[0.2em] block"
                        style={{ color: "var(--text-faint)" }}
                    >
                        {title}
                    </span>
                    {blurb ? (
                        <span
                            className="mono text-[11px] mt-1 block"
                            style={{ color: "var(--text-dim)" }}
                        >
                            {blurb}
                        </span>
                    ) : null}
                </span>
            </button>
            {open && (
                <div className="mt-3" data-testid={`section-body-${slug}`}>
                    {children}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Email template editor (Patch I)
// ---------------------------------------------------------------------------
const TEMPLATE_PLACEHOLDERS = [
    { name: "alert_type", hint: "e.g. Mobile Usage" },
    { name: "severity", hint: "e.g. High" },
    { name: "category", hint: "e.g. Safety" },
    { name: "driver_name", hint: "or 'Unassigned'" },
    { name: "driver_phone", hint: "formatted phone" },
    { name: "vehicle_name", hint: "or 'Unknown'" },
    { name: "location", hint: "address or coords" },
    { name: "triggered_at", hint: "ISO timestamp" },
    { name: "org_id", hint: "Samsara org id" },
    { name: "see_details_url", hint: "original Samsara link" },
    { name: "short_details_url", hint: "shortened link (preferred)" },
];

function EmailTemplateEditor({ descriptor, onSaved }) {
    const serverValue = descriptor?.value || "";
    const source = descriptor?.source || null;
    const isSet = !!descriptor?.is_set;

    const [draft, setDraft] = useState(serverValue);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [preview, setPreview] = useState(null); // { subject, body, warnings } | { error, detail }

    // Sync draft when server descriptor changes (e.g. after reload).
    useEffect(() => {
        setDraft(serverValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverValue]);

    const dirty = draft !== serverValue;

    const copyPlaceholder = async (name) => {
        const token = `{${name}}`;
        try {
            await navigator.clipboard.writeText(token);
            toast.success(`copied ${token}`, { duration: 1500 });
        } catch (e) {
            toast.error("copy failed — your browser blocked clipboard access");
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch("/admin/config", { EMAIL_TEMPLATE_PLAIN: draft });
            toast.success("email template saved", { duration: 2500 });
            await onSaved();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "save failed";
            toast.error(`template: ${msg}`);
        } finally {
            setSaving(false);
        }
    };

    const revert = async () => {
        setSaving(true);
        try {
            await api.patch("/admin/config", { EMAIL_TEMPLATE_PLAIN: "" });
            toast.success("template reverted to .env / built-in default", {
                duration: 2500,
            });
            setPreview(null);
            await onSaved();
        } catch (e) {
            const msg = e?.response?.data?.detail || e?.message || "revert failed";
            toast.error(`template: ${msg}`);
        } finally {
            setSaving(false);
        }
    };

    const resetToDefault = async () => {
        setResetting(true);
        try {
            const resp = await api.get("/admin/config/defaults");
            const def = resp?.data?.EMAIL_TEMPLATE_PLAIN || "";
            setDraft(def);
            toast.success("loaded built-in default (not yet saved)", {
                duration: 2500,
            });
        } catch (e) {
            toast.error(
                `defaults: ${e?.response?.data?.detail || e?.message || "failed"}`,
            );
        } finally {
            setResetting(false);
        }
    };

    const runPreview = async () => {
        setPreviewing(true);
        setPreview(null);
        try {
            const resp = await api.post("/admin/config/preview-email", {
                template: draft,
            });
            setPreview({
                subject: resp.data?.subject || "",
                body: resp.data?.body || "",
                warnings: resp.data?.warnings || [],
            });
        } catch (e) {
            const data = e?.response?.data;
            if (data?.error) {
                setPreview({ error: data.error, detail: data.detail || "" });
            } else {
                setPreview({
                    error: "preview failed",
                    detail: e?.message || "unknown error",
                });
            }
        } finally {
            setPreviewing(false);
        }
    };

    return (
        <div
            className="panel-inset p-4"
            data-testid="email-template-editor"
        >
            <div className="flex items-center gap-3 flex-wrap mb-3">
                <FileText size={14} style={{ color: "var(--accent)" }} />
                <span
                    className="mono text-[11px] uppercase tracking-[0.18em]"
                    style={{ color: "var(--text)" }}
                >
                    plaintext body template
                </span>
                <SourceBadge source={source} isSet={isSet} />
                {dirty && (
                    <span
                        className="mono text-[10px] uppercase tracking-[0.15em] px-2 py-[2px] rounded-full"
                        style={{
                            color: "var(--warn)",
                            border: "1px solid var(--warn)",
                        }}
                        data-testid="template-dirty-badge"
                    >
                        unsaved
                    </span>
                )}
            </div>

            <div
                className="mono text-[11px] mb-2"
                style={{ color: "var(--text-faint)" }}
            >
                Click any token to copy. Unknown tokens render as empty strings
                and surface as warnings below.
            </div>

            <div
                className="flex flex-wrap gap-1.5 mb-3"
                data-testid="template-placeholders"
            >
                {TEMPLATE_PLACEHOLDERS.map((p) => (
                    <button
                        type="button"
                        key={p.name}
                        onClick={() => copyPlaceholder(p.name)}
                        title={p.hint}
                        className="mono text-[11px] px-2 py-[3px] rounded-md inline-flex items-center gap-1 transition-colors"
                        style={{
                            border: "1px solid var(--border)",
                            color: "var(--text-dim)",
                            background: "transparent",
                        }}
                        data-testid={`placeholder-chip-${p.name}`}
                    >
                        <Copy size={10} />
                        {`{${p.name}}`}
                    </button>
                ))}
            </div>

            <textarea
                className="console-input"
                style={{
                    minHeight: 180,
                    fontFamily: "var(--font-mono, monospace)",
                    width: "100%",
                    resize: "vertical",
                    whiteSpace: "pre",
                    lineHeight: 1.45,
                }}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                spellCheck={false}
                placeholder="(empty → falls back to built-in default)"
                data-testid="template-textarea"
            />

            <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || !dirty}
                    className="pill-btn"
                    data-testid="template-save-btn"
                >
                    {saving ? (
                        <Loader2 size={11} className="animate-spin" />
                    ) : (
                        <Save size={11} />
                    )}{" "}
                    save
                </button>
                <button
                    type="button"
                    onClick={runPreview}
                    disabled={previewing}
                    className="ghost-btn"
                    data-testid="template-preview-btn"
                >
                    {previewing ? (
                        <Loader2 size={11} className="animate-spin" />
                    ) : (
                        <Play size={11} />
                    )}{" "}
                    preview with sample alert
                </button>
                <button
                    type="button"
                    onClick={resetToDefault}
                    disabled={resetting}
                    className="ghost-btn"
                    data-testid="template-reset-btn"
                    title="Load the built-in default into the editor (does not save)"
                >
                    {resetting ? (
                        <Loader2 size={11} className="animate-spin" />
                    ) : (
                        <RotateCcw size={11} />
                    )}{" "}
                    load default
                </button>
                {isSet && source === "db" && (
                    <button
                        type="button"
                        onClick={revert}
                        disabled={saving}
                        className="ghost-btn"
                        data-testid="template-revert-btn"
                        title="Clear DB override — revert to .env / built-in default"
                    >
                        <Xicon size={11} /> revert override
                    </button>
                )}
            </div>

            {preview && (
                <div
                    className="mt-4 panel-inset p-3"
                    style={{ borderColor: "var(--border-strong)" }}
                    data-testid="template-preview-panel"
                >
                    {preview.error ? (
                        <div data-testid="template-preview-error">
                            <div
                                className="mono text-[11px] uppercase tracking-[0.18em] mb-1"
                                style={{ color: "var(--danger, #d0021b)" }}
                            >
                                {preview.error}
                            </div>
                            <div
                                className="mono text-xs whitespace-pre-wrap break-all"
                                style={{ color: "var(--text-dim)" }}
                            >
                                {preview.detail}
                            </div>
                        </div>
                    ) : (
                        <>
                            <div
                                className="mono text-[10px] uppercase tracking-[0.18em] mb-1"
                                style={{ color: "var(--text-faint)" }}
                            >
                                subject
                            </div>
                            <div
                                className="mono text-xs mb-3 break-all"
                                style={{ color: "var(--info)" }}
                                data-testid="template-preview-subject"
                            >
                                {preview.subject}
                            </div>
                            <div
                                className="mono text-[10px] uppercase tracking-[0.18em] mb-1"
                                style={{ color: "var(--text-faint)" }}
                            >
                                body
                            </div>
                            <pre
                                className="mono text-xs p-2 rounded"
                                style={{
                                    color: "var(--text)",
                                    background: "var(--bg-inset, rgba(0,0,0,0.15))",
                                    border: "1px solid var(--border)",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                }}
                                data-testid="template-preview-body"
                            >
                                {preview.body || "(empty output)"}
                            </pre>
                            {preview.warnings && preview.warnings.length > 0 && (
                                <div
                                    className="mt-3"
                                    data-testid="template-preview-warnings"
                                >
                                    <div
                                        className="mono text-[10px] uppercase tracking-[0.18em] mb-1 inline-flex items-center gap-1"
                                        style={{ color: "var(--warn)" }}
                                    >
                                        <AlertTriangle size={11} />
                                        {preview.warnings.length} warning
                                        {preview.warnings.length === 1 ? "" : "s"}
                                    </div>
                                    <ul
                                        className="mono text-[11px] list-disc pl-5"
                                        style={{ color: "var(--warn)" }}
                                    >
                                        {preview.warnings.map((w, i) => (
                                            <li key={i}>{w}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Lockout-recovery docs panel (Patch K.1)
// Inline collapsible, default-closed-every-visit (no localStorage — this is
// help text, not a setting).
// ---------------------------------------------------------------------------
function LockoutRecoveryDocs({ publicBaseUrl }) {
    const [open, setOpen] = useState(false);
    const hub = (publicBaseUrl || "").replace(/\/$/, "") || "<your-hub>";
    const curl = `curl -X POST ${hub}/api/admin/password/reset \\
     -H "Content-Type: application/json" \\
     -H "X-Admin-Reset-Token: <the token you set>" \\
     -d '{"new_password":"<your new password, min 4 chars>"}'`;

    return (
        <div
            className="mb-3 rounded"
            style={{
                border: "1px dashed var(--warn)",
                background: "color-mix(in srgb, var(--warn) 6%, transparent)",
            }}
            data-testid="lockout-recovery-banner"
        >
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
                className="w-full flex items-center gap-2 px-3 py-2 text-left"
                style={{ background: "transparent", border: "none" }}
                data-testid="lockout-recovery-toggle"
            >
                <span style={{ color: "var(--warn)" }}>
                    {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                </span>
                <ShieldAlert size={12} style={{ color: "var(--warn)" }} />
                <span
                    className="mono text-[11px] uppercase tracking-[0.18em]"
                    style={{ color: "var(--warn)" }}
                >
                    how to recover a locked-out admin
                </span>
                <span
                    className="mono text-[10px] ml-auto"
                    style={{ color: "var(--text-faint)" }}
                >
                    {open ? "hide" : "5 steps"}
                </span>
            </button>

            {open && (
                <div
                    className="px-4 pb-4 pt-1 text-[12px]"
                    style={{
                        color: "var(--text-dim)",
                        fontFamily: "inherit",
                        lineHeight: 1.55,
                    }}
                    data-testid="lockout-recovery-content"
                >
                    <p className="mb-3">
                        If you&apos;ve forgotten the admin password and can&apos;t log in,
                        follow these 5 steps. The recovery endpoint is{" "}
                        <strong style={{ color: "var(--text)" }}>OFF by default</strong>{" "}
                        (status above shows{" "}
                        <span className="kbd">unset</span>) — you must explicitly
                        arm it by setting an env var, then disable it again after
                        recovery.
                    </p>

                    <ol
                        className="space-y-3 pl-5"
                        style={{ listStyle: "decimal" }}
                    >
                        <li>
                            <div>
                                <strong style={{ color: "var(--text)" }}>SET</strong>{" "}
                                a reset token in production env vars.
                            </div>
                            <div className="mt-1">
                                In the Emergent deployment dashboard, add an env
                                variable:
                            </div>
                            <pre
                                className="mono text-[11px] mt-1 p-2 rounded"
                                style={{
                                    color: "var(--text)",
                                    background: "rgba(0,0,0,0.25)",
                                    border: "1px solid var(--border)",
                                    whiteSpace: "pre-wrap",
                                }}
                            >
{"ADMIN_RESET_TOKEN = <any random string, e.g. `openssl rand -hex 16`>"}
                            </pre>
                            <div className="mt-1">
                                Save and redeploy. After deploy, the indicator
                                above will flip to{" "}
                                <span className="kbd">set · enabled</span>.
                            </div>
                        </li>

                        <li>
                            <div>
                                <strong style={{ color: "var(--text)" }}>RESET</strong>{" "}
                                the password (run from your terminal):
                            </div>
                            <pre
                                className="mono text-[11px] mt-1 p-2 rounded overflow-x-auto"
                                style={{
                                    color: "var(--text)",
                                    background: "rgba(0,0,0,0.25)",
                                    border: "1px solid var(--border)",
                                    whiteSpace: "pre",
                                }}
                                data-testid="lockout-recovery-curl"
                            >
                                {curl}
                            </pre>
                            <div className="mt-1">
                                Expected:{" "}
                                <span className="kbd">
                                    200 {"{"}&quot;ok&quot;: true, ...{"}"}
                                </span>
                            </div>
                        </li>

                        <li>
                            <strong style={{ color: "var(--text)" }}>LOG IN</strong>{" "}
                            with the new password.
                        </li>

                        <li>
                            <div>
                                <strong style={{ color: "var(--text)" }}>REMOVE</strong>{" "}
                                <span className="kbd">ADMIN_RESET_TOKEN</span> from
                                production env vars and redeploy.
                            </div>
                            <div className="mt-1">
                                This closes the recovery hatch. The reset endpoint
                                will return <span className="kbd">503</span> again.
                            </div>
                        </li>

                        <li>
                            <strong style={{ color: "var(--text)" }}>VERIFY</strong>{" "}
                            the hatch is closed by refreshing this page — the{" "}
                            <span className="kbd">ADMIN_RESET_TOKEN</span>{" "}
                            indicator above should be back to{" "}
                            <span className="kbd">unset</span>.
                        </li>
                    </ol>

                    <div
                        className="mt-4 pt-3"
                        style={{ borderTop: "1px solid var(--border)" }}
                    >
                        <div
                            className="mono text-[10px] uppercase tracking-[0.18em] mb-2"
                            style={{ color: "var(--warn)" }}
                        >
                            alternative recovery
                        </div>
                        <div className="mb-2">
                            If you forgot the password{" "}
                            <em>and</em> can&apos;t use the reset endpoint (e.g.
                            the redeploy didn&apos;t pick up Patch K), use the
                            env-override escape hatch:
                        </div>
                        <ol
                            className="space-y-2 pl-5"
                            style={{ listStyle: "decimal" }}
                        >
                            <li>
                                Set <em>both</em> env vars in production:{" "}
                                <span className="kbd">
                                    ADMIN_PASSWORD=&lt;your new password&gt;
                                </span>{" "}
                                and{" "}
                                <span className="kbd">
                                    ADMIN_PASSWORD_FORCE_ENV=1
                                </span>
                                . Redeploy.
                            </li>
                            <li>
                                Log in with the new password. A{" "}
                                <strong style={{ color: "var(--warn)" }}>
                                    recovery mode active
                                </strong>{" "}
                                banner will appear at the top of this page
                                confirming the override is live.
                            </li>
                            <li>
                                Settings → Auth → <em>change admin password</em>{" "}
                                — this updates the DB override to match.
                            </li>
                            <li>
                                Remove <em>both</em> env vars and redeploy to
                                disarm. The banner disappears; login now uses the
                                normal DB-over-env precedence.
                            </li>
                        </ol>
                    </div>

                    <div
                        className="mt-4 pt-3"
                        style={{ borderTop: "1px solid var(--border)" }}
                    >
                        <div
                            className="mono text-[10px] uppercase tracking-[0.18em] mb-2"
                            style={{ color: "var(--warn)" }}
                        >
                            security notes
                        </div>
                        <ul className="pl-5 space-y-1" style={{ listStyle: "disc" }}>
                            <li>
                                5 wrong tokens from one IP within 10 min → IP
                                locked out for 10 min.
                            </li>
                            <li>
                                Every successful reset is logged in the Audit Log
                                with{" "}
                                <span className="kbd">
                                    admin=&quot;emergency-reset&quot;
                                </span>
                                .
                            </li>
                            <li>
                                Never commit{" "}
                                <span className="kbd">ADMIN_RESET_TOKEN</span> to
                                source control.
                            </li>
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function SettingsPage() {
    const [cfg, setCfg] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const resp = await api.get("/admin/config");
            setCfg(resp.data);
        } catch (e) {
            toast.error("failed to load config");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const byKey = useMemo(() => {
        const m = {};
        (cfg?.fields || []).forEach((f) => {
            m[f.key] = f;
        });
        return m;
    }, [cfg]);

    if (loading || !cfg) {
        return (
            <section className="panel p-6" data-testid="settings-panel">
                <div
                    className="mono text-xs"
                    style={{ color: "var(--text-dim)" }}
                >
                    Loading settings…
                </div>
            </section>
        );
    }

    return (
        <section className="panel p-6 fade-in" data-testid="settings-panel">
            {cfg.admin_password_force_env && (
                <div
                    className="mb-5 px-4 py-3 rounded flex items-start gap-3"
                    style={{
                        border: "1px solid var(--warn)",
                        background:
                            "color-mix(in srgb, var(--warn) 12%, transparent)",
                    }}
                    data-testid="force-env-recovery-banner"
                    role="alert"
                >
                    <ShieldAlert
                        size={16}
                        style={{ color: "var(--warn)", flexShrink: 0, marginTop: 2 }}
                    />
                    <div className="flex-1 min-w-0">
                        <div
                            className="mono text-[11px] uppercase tracking-[0.18em] mb-1"
                            style={{ color: "var(--warn)" }}
                        >
                            recovery mode active
                        </div>
                        <div
                            className="text-[12px]"
                            style={{ color: "var(--text)", lineHeight: 1.5 }}
                        >
                            Admin password is being read from{" "}
                            <span className="kbd">.env</span>, bypassing the DB
                            override. Remove{" "}
                            <span className="kbd">ADMIN_PASSWORD_FORCE_ENV</span>{" "}
                            from production env and redeploy to restore normal
                            behavior.
                        </div>
                    </div>
                </div>
            )}

            <div className="flex items-center gap-3 mb-2">
                <SettingsIcon size={16} style={{ color: "var(--accent)" }} />
                <h2
                    className="mono text-sm uppercase tracking-[0.18em]"
                    style={{ color: "var(--text)" }}
                >
                    runtime config
                </h2>
            </div>
            <div
                className="mono text-[11px] mb-4"
                style={{ color: "var(--text-dim)" }}
            >
                Edits persist to Mongo and override <span className="kbd">.env</span>.
                IMAP changes hot-restart the poller; SMTP / webhook changes apply on
                the next send.
            </div>

            {/* Status strip */}
            <div className="flex flex-wrap gap-2 mb-2" data-testid="status-strip">
                <ImapStatusBadge cfg={cfg} />
                <span
                    className={`badge ${cfg.smtp_configured ? "ok" : "warn"}`}
                    data-testid="status-smtp"
                >
                    <span className="dot" />
                    SMTP {cfg.smtp_configured ? "configured" : "off"}
                </span>
                <span
                    className={`badge ${byKey.SAMSARA_WEBHOOK_SECRET?.is_set ? "ok" : "warn"}`}
                    data-testid="status-hmac"
                >
                    <span className="dot" />
                    HMAC {byKey.SAMSARA_WEBHOOK_SECRET?.is_set ? "enforced" : "skipped"}
                </span>
                <span
                    className={`badge ${byKey.RINGCENTRAL_WEBHOOK_URL?.is_set ? "ok" : "warn"}`}
                    data-testid="status-rc"
                >
                    <span className="dot" />
                    RC fallback {byKey.RINGCENTRAL_WEBHOOK_URL?.is_set ? "live" : "dry-run"}
                </span>
            </div>
            <ImapHealthLine cfg={cfg} />

            {/* Ingress path info */}
            <div
                className="panel-inset p-4 mb-2 flex items-center gap-3 flex-wrap"
                data-testid="ingress-url-row"
            >
                <Inbox size={14} style={{ color: "var(--accent)" }} />
                <span
                    className="mono text-[11px] uppercase tracking-[0.18em]"
                    style={{ color: "var(--text)" }}
                >
                    Samsara ingress URL
                </span>
                <span
                    className="mono text-xs break-all flex-1"
                    style={{ color: "var(--accent)" }}
                    data-testid="ingress-url"
                >
                    {(cfg.public_base_url || "").replace(/\/$/, "") +
                        cfg.ingress_path}
                </span>
            </div>

            {GROUPS.map((group) => (
                <CollapsibleSection
                    key={group.key}
                    slug={group.key}
                    title={group.title}
                    blurb={group.blurb}
                    defaultOpen={group.key === "core"}
                >
                    <div
                        className="flex flex-col gap-2"
                        data-testid={`group-${group.key}`}
                    >
                        {group.keys.map((field) => {
                            const desc = byKey[field.key] || {
                                key: field.key,
                                is_set: false,
                                is_secret: true,
                                source: null,
                            };
                            return (
                                <ConfigRow
                                    key={field.key}
                                    field={field}
                                    descriptor={desc}
                                    onSaved={load}
                                />
                            );
                        })}
                        {group.key === "imap" && (
                            <div className="mt-1">
                                <TestButton
                                    label="IMAP"
                                    endpoint="/admin/config/test/imap"
                                    testid="test-imap-btn"
                                />
                            </div>
                        )}
                        {group.key === "smtp" && (
                            <div className="mt-1">
                                <TestButton
                                    label="SMTP"
                                    endpoint="/admin/config/test/smtp"
                                    testid="test-smtp-btn"
                                />
                            </div>
                        )}
                    </div>
                </CollapsibleSection>
            ))}

            <CollapsibleSection
                slug="email_templates"
                title="email template"
                blurb="Plaintext body used for email-type channel deliveries. HTML sibling stays hard-coded for now."
            >
                <EmailTemplateEditor
                    descriptor={byKey.EMAIL_TEMPLATE_PLAIN}
                    onSaved={load}
                />
            </CollapsibleSection>

            <CollapsibleSection
                slug="auth"
                title="auth"
                blurb="Single admin password. JWT_SECRET stays in .env (rotating it would log everyone out)."
            >
                <div
                    className="mb-3 px-3 py-2 rounded text-[12px] flex items-start gap-2"
                    style={{
                        border: "1px solid var(--info)",
                        background:
                            "color-mix(in srgb, var(--info) 8%, transparent)",
                        color: "var(--text)",
                        lineHeight: 1.5,
                    }}
                    data-testid="dual-source-auth-info"
                >
                    <ShieldCheck
                        size={14}
                        style={{ color: "var(--info)", flexShrink: 0, marginTop: 2 }}
                    />
                    <span>
                        Admin login accepts <strong>either</strong> the
                        env-configured password <strong>or</strong> the
                        DB-stored password (set via &quot;change admin
                        password&quot; below). This is intentional for
                        lockout recovery — edit the production{" "}
                        <span className="kbd">ADMIN_PASSWORD</span> env var to
                        a known value and redeploy to regain access.
                    </span>
                </div>

                <div
                    className="panel-inset p-4 mb-2 flex items-center gap-3 flex-wrap"
                    data-testid="admin-password-status"
                >
                    <KeyRound
                        size={14}
                        style={{
                            color: byKey.ADMIN_PASSWORD?.is_set
                                ? "var(--accent)"
                                : "var(--warn)",
                        }}
                    />
                    <span
                        className="mono text-[11px] uppercase tracking-[0.18em]"
                        style={{ color: "var(--text)", minWidth: 220 }}
                    >
                        ADMIN_PASSWORD
                    </span>
                    <SourceBadge
                        source={byKey.ADMIN_PASSWORD?.source}
                        isSet={byKey.ADMIN_PASSWORD?.is_set}
                    />
                    <span
                        className="mono text-xs"
                        style={{
                            color: byKey.ADMIN_PASSWORD?.is_set
                                ? "var(--text-dim)"
                                : "var(--warn)",
                        }}
                    >
                        {byKey.ADMIN_PASSWORD?.is_set
                            ? "set · login enabled"
                            : "unset · login blocked"}
                    </span>
                </div>

                {/* Patch K — emergency reset token (read-only status) */}
                <div
                    className="panel-inset p-4 mb-2 flex items-center gap-3 flex-wrap"
                    data-testid="admin-reset-token-status"
                >
                    <ShieldAlert
                        size={14}
                        style={{
                            color: cfg.admin_reset_token_set
                                ? "var(--warn)"
                                : "var(--text-faint)",
                        }}
                    />
                    <span
                        className="mono text-[11px] uppercase tracking-[0.18em]"
                        style={{ color: "var(--text)", minWidth: 220 }}
                    >
                        ADMIN_RESET_TOKEN
                    </span>
                    <span
                        className="mono text-[10px] uppercase tracking-[0.15em] px-2 py-[2px] rounded-full"
                        style={{
                            color: cfg.admin_reset_token_set
                                ? "var(--warn)"
                                : "var(--text-faint)",
                            border: `1px solid ${
                                cfg.admin_reset_token_set
                                    ? "var(--warn)"
                                    : "var(--border)"
                            }`,
                        }}
                        data-testid="admin-reset-token-badge"
                    >
                        {cfg.admin_reset_token_set ? "set · enabled" : "unset"}
                    </span>
                    <span
                        className="mono text-xs flex-1"
                        style={{
                            color: cfg.admin_reset_token_set
                                ? "var(--warn)"
                                : "var(--text-faint)",
                        }}
                    >
                        {cfg.admin_reset_token_set
                            ? "emergency reset endpoint is LIVE — remove env var when not actively recovering"
                            : "emergency reset is disabled (off-by-default)"}
                    </span>
                </div>

                {/* Lockout-recovery docs (Patch K.1 — collapsible) */}
                <LockoutRecoveryDocs publicBaseUrl={cfg.public_base_url} />

                <PasswordChange />
            </CollapsibleSection>

            <CollapsibleSection
                slug="audit_log"
                title="audit log"
                blurb="History of config changes — who changed which key, when, and masked values."
            >
                <AuditLog />
            </CollapsibleSection>
        </section>
    );
}
